import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { login as apiLogin, register as apiRegister, authenticatedApiCall } from '../api.ts';

// Define a specific type for the user object
interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  is_active: boolean;
  is_admin: boolean;
  has_car: boolean;
  car_motorization: string | null;
  has_motorcycle: boolean;
  needs_pmr: boolean;
  personal_constraints: string | null;
}

// Define a type for the registration data
interface UserRegistrationData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  has_car?: boolean;
  car_motorization?: string;
  has_motorcycle?: boolean;
  needs_pmr?: boolean;
  personal_constraints?: string;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  register: (userData: UserRegistrationData) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Ajout de l'état de chargement

  const isAuthenticated = !!token;
  const isAdmin = user?.is_admin || false;

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      // Fetch user data when token is available
      const fetchUser = async () => {
        try {
          const currentUser = await authenticatedApiCall('GET', '/users/me/', token);
          setUser(currentUser);
        } catch (error) {
          console.error("Failed to fetch user data:", error);
          // Déconnecter si les données utilisateur ne peuvent être récupérées (ex: token expiré)
          setToken(null);
          setUser(null);
          localStorage.removeItem('token');
        } finally {
          setIsLoading(false);
        }
      };
      fetchUser();
    } else {
      localStorage.removeItem('token');
      setUser(null);
      setIsLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (username: string, password: string) => {
    const data = await apiLogin(username, password);
    setToken(data.access_token);
  };

  const register = async (userData: UserRegistrationData) => {
    await apiRegister(userData);
    // Optionally, log in the user after successful registration
    // await login(userData.username, userData.password);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ token, user, login, register, logout, isAuthenticated, isAdmin, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
